import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import { normalizeTemplateBody } from "../../../../../../utils/templateBodyValidation";
import { hydrateEditor } from "./WhatsAppBodyEditor.hydration";

import formatBoldIcon from "../../../../../../assets/svg/Format_bold.svg";
import formatItalicIcon from "../../../../../../assets/svg/Format_italic.svg";
import formatStrikeThroughIcon from "../../../../../../assets/svg/Strikethrough.svg";

import {
  CharCounter,
  ChipTooltip,
  CreateVariableButton,
  EditorArea,
  EditorContainer,
  ErrorMessage,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
} from "./WhatsAppBodyEditor.style";

// ─── Constants ────────────────────────────────────────────────────────────────

const BLOCK_TAGS = new Set([
  "p",
  "div",
  "li",
  "tr",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
]);

// ─── DOM Walker Helpers ───────────────────────────────────────────────────────

/**
 * A block boundary is one newline; empty blocks remain empty lines. Inline
 * formatting stays intact instead of splitting serialized HTML across tags.
 * In editable DOM, the last direct BR of a block/root is a cursor anchor.
 * Clipboard BRs are content, except the sole BR of an empty paragraph.
 */
function serializeChildren(parent, convert, separator, editable = true) {
  const nodes = Array.from(parent.childNodes);
  const blocks = [];
  let inline = "";
  const blockParent = BLOCK_TAGS.has(parent.nodeName.toLowerCase());
  const root =
    parent.getAttribute && parent.getAttribute("contenteditable") === "true";
  nodes.forEach((node, index) => {
    const tag = node.nodeName.toLowerCase();
    if (
      tag === "br" &&
      index === nodes.length - 1 &&
      ((editable && (blockParent || root)) ||
        (blockParent && nodes.length === 1))
    ) {
      return;
    }
    if (BLOCK_TAGS.has(tag)) {
      if (inline) {
        // A direct BR already supplies the following block boundary.
        const previous = nodes[index - 1];
        const endsWithBreak =
          previous && previous.nodeName.toLowerCase() === "br";
        blocks.push(
          endsWithBreak ? inline.slice(0, -separator.length) : inline,
        );
        inline = "";
      }
      blocks.push(convert(node));
    } else {
      inline += convert(node);
    }
  });
  if (inline) blocks.push(inline);
  return blocks.join(separator);
}

/** Edit boundary nodes in place, retaining chip/format nodes and live ranges. */
function trimBodyDom(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node.parentElement?.closest(".variable-chip")) continue;
    const normalized = node.data.replace(/\r\n?/g, "\n");
    if (normalized !== node.data) node.data = normalized;
  }
  const trimEdge = (parent, edge) => {
    while (parent[edge]) {
      const node = parent[edge];
      if (node.nodeType === Node.TEXT_NODE) {
        const trimmed =
          edge === "firstChild" ? node.data.trimStart() : node.data.trimEnd();
        const removed = node.length - trimmed.length;
        if (removed)
          node.deleteData(edge === "firstChild" ? 0 : trimmed.length, removed);
        if (node.length) return;
      } else if (BLOCK_TAGS.has(node.nodeName.toLowerCase())) {
        trimEdge(node, edge);
        if (node.childNodes.length) return;
      } else if (node.nodeName !== "BR") {
        return; // Markdown markers and placeholders are meaningful boundaries.
      }
      parent.removeChild(node);
    }
  };
  trimEdge(root, "firstChild");
  trimEdge(root, "lastChild");
}

/** Normalize safe, flattened preview HTML without treating literal text as markup. */
export function normalizeBodyHtml(html) {
  const root = document.createElement("div");
  root.appendChild(hydrateEditor({ html }));
  const flat = document.createElement("div");
  flat.appendChild(hydrateEditor({ html: extractHtmlFromDom(root).html }));
  trimBodyDom(flat);
  return {
    ...extractMarkdownFromDom(flat),
    ...extractHtmlFromDom(flat),
    text: extractMarkdownFromDom(flat, true).markdown,
  };
}

function wrapEditorHtml(content) {
  if (!content) return "";
  // A terminal content break needs a second BR as the editable cursor anchor.
  const anchor = content.endsWith("<br>") ? "<br>" : "";
  return `<p>${content}${anchor}</p>`;
}

/**
 * Walk the DOM tree and extract WhatsApp markdown text.
 * Returns { markdown: string, variables: string[] }
 */
function extractMarkdownFromDom(rootNode, visible = false) {
  const processedVars = new Map(); // varName → 1-based index
  const variablesList = []; // ordered unique variable names

  const convertInline = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const tag = node.tagName.toLowerCase();

    if (tag === "br") return "\n";

    if (tag === "span" && node.classList.contains("variable-chip")) {
      const varName =
        node.getAttribute("data-variable-name") || node.textContent.trim();
      if (!processedVars.has(varName)) {
        processedVars.set(varName, processedVars.size + 1);
        variablesList.push(varName);
      }
      return visible ? varName : `{{${processedVars.get(varName)}}}`;
    }

    if (tag === "script" || tag === "style") return "";
    const children = serializeChildren(node, convertInline, "\n");
    if (visible) return children;

    if (tag === "b" || tag === "strong") return `*${children}*`;
    if (tag === "i" || tag === "em") return `_${children}_`;
    if (tag === "s" || tag === "del" || tag === "strike")
      return `~${children}~`;
    if (tag === "code") return `\`\`\`${children}\`\`\``;

    return children;
  };

  const markdown = serializeChildren(rootNode, convertInline, "\n");
  return { markdown, variables: variablesList };
}

/**
 * Walk the DOM tree and extract clean HTML for preview.
 * Returns { html: string }
 */
function extractHtmlFromDom(rootNode, clipboard = false) {
  const convertInline = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent);
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const tag = node.tagName.toLowerCase();

    if (tag === "br") return "<br>";

    if (
      !clipboard &&
      tag === "span" &&
      node.classList.contains("variable-chip")
    ) {
      const varName =
        node.getAttribute("data-variable-name") || node.textContent.trim();
      const attributeName = escapeHtml(varName).replace(/"/g, "&quot;");
      return `<span class="variable-chip" contenteditable="false" data-variable-name="${attributeName}">${escapeHtml(varName)}</span>`;
    }

    if (tag === "script" || tag === "style") return "";
    const children = serializeChildren(node, convertInline, "<br>", !clipboard);

    if (tag === "b" || tag === "strong") return `<b>${children}</b>`;
    if (tag === "i" || tag === "em") return `<i>${children}</i>`;
    if (tag === "s" || tag === "del" || tag === "strike")
      return `<s>${children}</s>`;
    if (tag === "code") return `<code>${children}</code>`;

    return children;
  };

  const content = serializeChildren(
    rootNode,
    convertInline,
    "<br>",
    !clipboard,
  );
  const html = wrapEditorHtml(content);

  return { html };
}

// ─── Utility ──────────────────────────────────────────────────────────────────

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Check if the given selection is (at least partially) inside targetNode.
 */
function isSelectionInside(selection, targetNode) {
  if (!selection || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  return (
    targetNode.contains(range.startContainer) &&
    targetNode.contains(range.endContainer)
  );
}

/**
 * Get the nearest ancestor matching the tag from a node, stopping at boundary.
 */
function getAncestorWithTag(node, tagName, boundary) {
  let current = node;
  while (current && current !== boundary) {
    if (
      current.nodeType === Node.ELEMENT_NODE &&
      current.tagName.toLowerCase() === tagName
    ) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
}

/**
 * Unwrap a node: replace it with its children in the DOM.
 */
function unwrapNode(node) {
  const parent = node.parentNode;
  if (!parent) return;
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
}

/**
 * Wrap the current Range selection in the given tag element.
 * If the selection is already fully inside that tag, unwrap instead.
 */
function toggleFormatOnSelection(tagName, editorRef) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  if (!isSelectionInside(selection, editorRef.current)) return;

  const range = selection.getRangeAt(0);

  // Check if entire selection is inside an existing tag of this type
  const ancestorTag = getAncestorWithTag(
    range.commonAncestorContainer,
    tagName,
    editorRef.current,
  );

  if (ancestorTag) {
    // Unwrap: remove the format tag, keep its content
    unwrapNode(ancestorTag);
  } else {
    // Wrap: extract the range contents into a new element
    if (range.collapsed) return; // nothing selected, nothing to wrap

    const wrapper = document.createElement(tagName);
    try {
      range.surroundContents(wrapper);
    } catch (_e) {
      // surroundContents throws if selection crosses element boundaries
      // Fall back to extractContents + insertNode
      const fragment = range.extractContents();
      wrapper.appendChild(fragment);
      range.insertNode(wrapper);
    }

    // Restore selection inside the wrapper
    const newRange = document.createRange();
    newRange.selectNodeContents(wrapper);
    selection.removeAllRanges();
    selection.addRange(newRange);
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * WhatsAppBodyEditor — standalone rich-text editor for WhatsApp message bodies.
 *
 * Props:
 *   initialHtml        {string}   HTML to restore (from broadcastData.bodyMessageWithFormat)
 *   initialVariables   {string[]} Ordered variable names matching {{N}} placeholders
 *   initialText        {string}   Literal fallback when initialHtml is absent
 *   identifyNames      {boolean}  Identify plain variable names on initialization
 *   allowVariables     {boolean}  Default true. When false (a template with
 *                                 no variables at all) the editor offers no
 *                                 «Crear campo», creates no chip, and shows
 *                                 any saved chip or placeholder as plain text.
 *   onChangeText       {(text: string) => void} Visible text, including when invalid
 *   onChangeError      {(overLimit: boolean) => void}
 *   onChangeMarkdown   {(markdown: string) => void}
 *   onChangeHtml       {(html: string) => void}
 *   onChangeVariables  {(variables: string[]) => void}
 *   maxLength          {number}   Default 1024
 *   error              {boolean}  External error state
 *   errorMessage       {string}   Error text to display
 */
function WhatsAppBodyEditor({
  bodyEditorRef,
  initialHtml,
  initialVariables,
  initialText,
  identifyNames = false,
  allowVariables = true,
  onChangeText,
  onChangeError,
  onChangeMarkdown,
  onChangeHtml,
  onChangeVariables,
  maxLength = 1024,
  error = false,
  errorMessage,
}) {
  const editorRef = useRef(null);
  const initializedRef = useRef(false);

  const [showCreateVariable, setShowCreateVariable] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [internalError, setInternalError] = useState(false);
  const [activeFormats, setActiveFormats] = useState({
    b: false,
    i: false,
    s: false,
    code: false,
  });
  const [chipTooltip, setChipTooltip] = useState(null);

  // ── Extract & emit ──────────────────────────────────────────────────────────

  const extractAndEmit = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const { markdown, variables } = extractMarkdownFromDom(editor);
    const textLen = normalizeTemplateBody(markdown).length;
    setCharCount(textLen);
    const overLimit = textLen > maxLength;
    setInternalError(overLimit);

    const { html } = extractHtmlFromDom(editor);

    // Order matters to consumers: the visible text is emitted last, so a
    // consumer that validates on it already has the markdown and the variables.
    if (onChangeMarkdown) onChangeMarkdown(markdown);
    if (onChangeHtml) onChangeHtml(html);
    if (onChangeVariables) onChangeVariables(variables);
    if (onChangeText)
      onChangeText(extractMarkdownFromDom(editor, true).markdown);
    if (onChangeError) onChangeError(overLimit);
  }, [
    maxLength,
    onChangeMarkdown,
    onChangeHtml,
    onChangeVariables,
    onChangeText,
    onChangeError,
  ]);

  useImperativeHandle(bodyEditorRef, () => ({
    normalize() {
      const editor = editorRef.current;
      const before = extractMarkdownFromDom(editor).markdown;
      // Explicit Continue boundary only; ordinary typing never rewrites DOM.
      if (before !== normalizeTemplateBody(before)) trimBodyDom(editor);
      extractAndEmit();
      return {
        ...extractMarkdownFromDom(editor),
        ...extractHtmlFromDom(editor),
        text: extractMarkdownFromDom(editor, true).markdown,
      };
    },
  }));

  // ── Initialization ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (initializedRef.current) return;
    if (!editorRef.current) return;

    initializedRef.current = true;
    editorRef.current.textContent = "";
    editorRef.current.appendChild(
      hydrateEditor({
        html: initialHtml,
        text: initialText,
        variables: initialVariables,
        identifyNames,
        allowVariables,
      }),
    );
    extractAndEmit();
  }, [
    initialHtml,
    initialText,
    initialVariables,
    identifyNames,
    allowVariables,
    extractAndEmit,
  ]);

  // ── Selection tracking ──────────────────────────────────────────────────────

  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    const editor = editorRef.current;

    if (
      selection &&
      !selection.isCollapsed &&
      editor &&
      isSelectionInside(selection, editor)
    ) {
      setShowCreateVariable(true);
    } else {
      setShowCreateVariable(false);
    }

    // Detect active formats at cursor position
    if (selection && selection.rangeCount > 0 && editor) {
      const node = selection.focusNode;
      setActiveFormats({
        b:
          !!getAncestorWithTag(node, "b", editor) ||
          !!getAncestorWithTag(node, "strong", editor),
        i:
          !!getAncestorWithTag(node, "i", editor) ||
          !!getAncestorWithTag(node, "em", editor),
        s:
          !!getAncestorWithTag(node, "s", editor) ||
          !!getAncestorWithTag(node, "del", editor) ||
          !!getAncestorWithTag(node, "strike", editor),
        code: !!getAncestorWithTag(node, "code", editor),
      });
    } else {
      setActiveFormats({ b: false, i: false, s: false, code: false });
    }
  }, []);

  // ── Format toggle ───────────────────────────────────────────────────────────

  const handleToggleFormat = useCallback(
    (tagName) => {
      const selection = window.getSelection();
      if (!selection || !editorRef.current) return;
      if (!isSelectionInside(selection, editorRef.current)) {
        editorRef.current.focus();
        return;
      }

      toggleFormatOnSelection(tagName, editorRef);
      editorRef.current.focus();
      extractAndEmit();
      handleSelectionChange();
    },
    [extractAndEmit, handleSelectionChange],
  );

  // ── Variable creation ───────────────────────────────────────────────────────

  const handleCreateVariable = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    if (!isSelectionInside(selection, editorRef.current)) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    const selectedText = range.toString().trim();
    if (!selectedText) return;

    // Delete selected content and insert a chip
    range.deleteContents();

    const chip = document.createElement("span");
    chip.className = "variable-chip";
    chip.setAttribute("contenteditable", "false");
    chip.setAttribute("data-variable-name", selectedText);
    chip.textContent = selectedText;
    range.insertNode(chip);

    // Move cursor after the chip
    const afterRange = document.createRange();
    afterRange.setStartAfter(chip);
    afterRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(afterRange);

    setShowCreateVariable(false);
    extractAndEmit();
  }, [extractAndEmit]);

  // ── Variable deletion (click on chip) ──────────────────────────────────────

  const handleEditorClick = useCallback(
    (e) => {
      const target = e.target;
      if (
        target.nodeType === Node.ELEMENT_NODE &&
        target.classList.contains("variable-chip")
      ) {
        setChipTooltip(null);
        const varName =
          target.getAttribute("data-variable-name") ||
          target.textContent.trim();
        const textNode = document.createTextNode(varName);
        target.parentNode.replaceChild(textNode, target);
        extractAndEmit();
      }
    },
    [extractAndEmit],
  );

  // ── Chip tooltip (mouseover delegation) ────────────────────────────────────

  const handleEditorMouseOver = useCallback((e) => {
    const target = e.target;
    if (
      target.nodeType === Node.ELEMENT_NODE &&
      target.classList.contains("variable-chip")
    ) {
      const rect = target.getBoundingClientRect();
      setChipTooltip({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    }
  }, []);

  const handleEditorMouseOut = useCallback((e) => {
    const target = e.target;
    if (
      target.nodeType === Node.ELEMENT_NODE &&
      target.classList.contains("variable-chip")
    ) {
      setChipTooltip(null);
    }
  }, []);

  // ── Paste handling ──────────────────────────────────────────────────────────

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();

      const html = e.clipboardData.getData("text/html");
      const plain = e.clipboardData.getData("text/plain");

      let insertHtml;

      if (html) {
        const doc = new DOMParser().parseFromString(html, "text/html");

        insertHtml = extractHtmlFromDom(doc.body, true).html;
      } else {
        // Escape before introducing markup; retain spaces and every empty line.
        insertHtml = wrapEditorHtml(
          escapeHtml(plain).replace(/\r\n?/g, "\n").replace(/\n/g, "<br>"),
        );
      }

      if (insertHtml) {
        // execCommand('insertHTML') is the one acceptable use — it maintains undo history
        document.execCommand("insertHTML", false, insertHtml);
      }

      extractAndEmit();
    },
    [extractAndEmit],
  );

  // ── Input handler ───────────────────────────────────────────────────────────

  const handleInput = useCallback(() => {
    extractAndEmit();
  }, [extractAndEmit]);

  // ── Computed state ──────────────────────────────────────────────────────────

  const showError = error || internalError;
  const effectiveErrorMessage =
    errorMessage ||
    (internalError
      ? `El mensaje no puede superar ${maxLength} caracteres.`
      : null);

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarButton
          type="button"
          title="Negrita (*texto*)"
          $active={activeFormats.b}
          onClick={() => handleToggleFormat("b")}
        >
          <img src={formatBoldIcon} alt="Negrita" />
        </ToolbarButton>

        <ToolbarButton
          type="button"
          title="Cursiva (_texto_)"
          $active={activeFormats.i}
          onClick={() => handleToggleFormat("i")}
        >
          <img src={formatItalicIcon} alt="Cursiva" />
        </ToolbarButton>

        <ToolbarButton
          type="button"
          title="Tachado (~texto~)"
          $active={activeFormats.s}
          onClick={() => handleToggleFormat("s")}
        >
          <img src={formatStrikeThroughIcon} alt="Tachado" />
        </ToolbarButton>

        <ToolbarButton
          type="button"
          title="Monoespaciado (```texto```)"
          $active={activeFormats.code}
          onClick={() => handleToggleFormat("code")}
          style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
        >
          {"{ }"}
        </ToolbarButton>

        {allowVariables && showCreateVariable && (
          <>
            <ToolbarDivider />
            <CreateVariableButton type="button" onClick={handleCreateVariable}>
              Crear campo
            </CreateVariableButton>
          </>
        )}
      </Toolbar>

      <EditorArea
        contentEditable
        ref={editorRef}
        $error={showError}
        data-placeholder="Escribe el mensaje aquí..."
        onMouseUp={handleSelectionChange}
        onKeyUp={handleSelectionChange}
        onInput={handleInput}
        onPaste={handlePaste}
        onClick={handleEditorClick}
        onMouseOver={handleEditorMouseOver}
        onMouseOut={handleEditorMouseOut}
        suppressContentEditableWarning
      />

      {chipTooltip && (
        <ChipTooltip $top={chipTooltip.top} $left={chipTooltip.left}>
          Click para convertir a texto
        </ChipTooltip>
      )}

      <CharCounter $overLimit={charCount > maxLength}>
        {charCount} / {maxLength}
      </CharCounter>

      {showError && effectiveErrorMessage && (
        <ErrorMessage>{effectiveErrorMessage}</ErrorMessage>
      )}
    </EditorContainer>
  );
}

export default WhatsAppBodyEditor;
